import { filterParams } from 'components/SearchFilter/filterOptions';
import { AlgoliaFilterAttributes } from 'constants/algolia';
import { typeTrackingSearch } from 'constants/search';
import { transportMethods } from '../analytics.constants';

export type LocationRecommended = 'collection' | 'closedSearch';

type Filter = {
  date?: string;
  price?: string;
  type?: string;
  order?: string;
  collections?: string;
};

export type GetParamsSearchEvent = {
  eventId: number | string;
  eventName: string;
  displayedCount: number;
  isInFirstViewport: boolean;
  location: string;
  position: number;
  resultRank: number;
  currentSortBy?: string;
  eventCategory?: string;
  filter?: Filter;
  locationRecommended?: LocationRecommended;
  resultsCount?: number;
  searchTerm?: string;
  title?: string;
};

const PRICE_LABELS: Record<string, string> = {
  true: 'Gratuito',
  false: 'Pago'
};
export const getLabelPrice = (value: string): string =>
  PRICE_LABELS[value] ?? '';

export const extractFilterValue = (filters: string[], filterName: string) => {
  const value =
    filters.find((f) => f.startsWith(`${filterName}:`))?.split(':')[1] || '';
  if (filterName === AlgoliaFilterAttributes.IS_ACCESSIBLE_FOR_FREE) {
    return getLabelPrice(value);
  }
  return value;
};

const FILTER_KEY_MAPPINGS: Record<string, string> = {
  [AlgoliaFilterAttributes.IS_ACCESSIBLE_FOR_FREE]: filterParams.price.title,
  [AlgoliaFilterAttributes.THEME]: filterParams.collections.title,
  [AlgoliaFilterAttributes.TYPE]: filterParams.type.title,
  [AlgoliaFilterAttributes.DATE]: filterParams.date.title
};

const buildAppliedFiltersString = (filter?: Filter): string =>
  [
    filter?.date && `${filterParams.date.title}:${filter.date}`,
    filter?.price &&
      `${filterParams.price.title}:${getLabelPrice(filter.price)}`,
    filter?.collections &&
      `${filterParams.collections.title}:${filter.collections}`
  ]
    .filter(Boolean)
    .join('|');

export const treatArrayFilter = (filters: string[]): string[] =>
  filters.map((filter) => {
    const [key, value] = filter.split(':');
    const mappedKey = FILTER_KEY_MAPPINGS[key];

    if (!mappedKey) return filter;

    const formattedValue =
      key === AlgoliaFilterAttributes.IS_ACCESSIBLE_FOR_FREE
        ? getLabelPrice(value)
        : value;

    return `${mappedKey}:${formattedValue}`;
  });

export const getParamsClickRecommendedEvent = ({
  eventId,
  eventName,
  position,
  location,
  locationRecommended,
  filter,
  title,
  displayedCount,
  resultRank,
  isInFirstViewport
}: GetParamsSearchEvent) => {
  return {
    event_id: String(eventId),
    event_name: eventName,
    item_filter_date: filter?.date || '',
    item_filter_price: filter?.price || '',
    item_filter_type: filter?.type || '',
    item_filter_order: filter?.order || '',
    item_location_set: location,
    is_in_first_viewport: isInFirstViewport,
    item_result_rank: resultRank || position,
    ...(locationRecommended === typeTrackingSearch.collection && {
      item_collection_title: title?.trim(),
      item_rail_count: displayedCount
    }),
    ...(locationRecommended === typeTrackingSearch.closedSearch && {
      item_rail_title: title?.trim(),
      item_event_total_count: displayedCount,
      item_event_position: position
    }),
    transport: transportMethods.sendBeacon
  };
};

export const getParamsSearchEventClick = ({
  eventId,
  eventName,
  searchTerm,
  location,
  filter,
  resultsCount,
  displayedCount,
  currentSortBy,
  isInFirstViewport,
  resultRank,
  eventCategory
}: GetParamsSearchEvent) => ({
  event_id: String(eventId),
  event_name: eventName,
  search_term: searchTerm,
  search_location_set: location,
  search_applied_filters_on_page: buildAppliedFiltersString(filter),
  search_results_count: resultsCount,
  search_results_displayed_count: displayedCount,
  search_current_sort_by: currentSortBy,
  is_in_first_viewport: isInFirstViewport,
  search_result_event_id: String(eventId),
  search_result_rank: resultRank,
  search_event_category: eventCategory
});

export const getParamsRailClicked = ({
  eventName,
  eventId,
  pageTitleForTracking,
  nameRail,
  filters
}: {
  eventName: string;
  eventId: number | string;
  pageTitleForTracking?: string;
  nameRail?: string;
  filters?: string;
}) => ({
  event_id: String(eventId),
  event_name: eventName,
  item_rail_location: pageTitleForTracking?.trim(),
  item_rail_title: nameRail?.trim(),
  item_rail_filter: filters || '',
  transport: transportMethods.sendBeacon
});
